import { Chart, Plugin } from 'chart.js';

export interface SitePerformanceChartHoverState {
  siteIndex: number | null;
  seriesIndex: number | null;
}

export type SitePerformanceChartHoverChangeHandler = (hover: SitePerformanceChartHoverState) => void;

const EMPTY_HOVER_STATE: SitePerformanceChartHoverState = {
  siteIndex: null,
  seriesIndex: null,
};

const chartHoveredSiteIndexMap = new WeakMap<Chart, number | null>();

export const getSitePerformanceChartJsHoveredSiteIndex = (chart: Chart): number | null =>
  chartHoveredSiteIndexMap.get(chart) ?? null;

export const setSitePerformanceChartJsHoveredSiteIndex = (chart: Chart, siteIndex: number | null): void => {
  chartHoveredSiteIndexMap.set(chart, siteIndex);
};

export const createSitePerformanceChartJsBarHoverPlugin = (
  onHoverChange: SitePerformanceChartHoverChangeHandler
): Plugin<'bar'> => ({
  id: 'sitePerformanceChartJsBarHover',
  afterEvent(chart: Chart, args): void {
    if (args.replay) {
      return;
    }

    const { event } = args;

    if (
      event.type === 'mouseout' ||
      event.x == null ||
      event.y == null ||
      !chart.isPointInArea({ x: event.x, y: event.y })
    ) {
      setSitePerformanceChartJsHoveredSiteIndex(chart, null);
      onHoverChange(EMPTY_HOVER_STATE);

      return;
    }

    const nativeEvent = event.native;

    if (!nativeEvent) {
      setSitePerformanceChartJsHoveredSiteIndex(chart, null);
      onHoverChange(EMPTY_HOVER_STATE);

      return;
    }

    const elements = chart.getElementsAtEventForMode(nativeEvent, 'index', { axis: 'y', intersect: false }, false);
    const activeElement = chart.getActiveElements()[0];
    const siteIndex = elements[0]?.index ?? null;

    setSitePerformanceChartJsHoveredSiteIndex(chart, siteIndex);
    onHoverChange({
      siteIndex,
      seriesIndex: activeElement?.datasetIndex ?? elements[0]?.datasetIndex ?? null,
    });
  },
});
